import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Camera, Loader2, CheckCircle2, AlertTriangle, Upload } from 'lucide-react';
import { shrinkImage, uploadError } from '../../components/regSession';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Phone page opened from the QR on the family's computer: take the photo here, it
// attaches to the registration open on the computer. No sign-in — the link is the key.
export default function RegPhotoPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState('');
  const [shot, setShot] = useState(null);   // {file, url}
  const [busy, setBusy] = useState(false);
  const [sendErr, setSendErr] = useState('');
  const [done, setDone] = useState(false);
  const camRef = useRef(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/reg-family/scan/${token}`)
      .then(r => { setInfo(r.data); if (r.data.done) setDone(true); })
      .catch(e => setErr(e?.response?.data?.detail || 'This link is invalid or has expired.'));
  }, [token]);

  const send = async () => {
    if (!shot) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', await shrinkImage(shot.file));
      await axios.post(`${API_URL}/api/reg-family/scan/${token}/upload`, fd);
      setDone(true);
    } catch (e) { setSendErr(uploadError(e)); }
    finally { setBusy(false); }
  };

  const b = info?.brand || {};
  const brand = b.primary_color || '#b45309';
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 px-4 py-6" data-testid="reg-photo-page">
      <div className="max-w-md mx-auto space-y-4">
        <div className="flex items-center gap-3">
          {b.logo_url && <img src={b.logo_url.startsWith('http') ? b.logo_url : `${API_URL}${b.logo_url}`} alt="" className="h-10 w-10 object-contain" />}
          <div className="font-semibold">{b.org_name}</div>
        </div>
        {err ? (
          <div className="rounded-xl border bg-white dark:bg-slate-800 p-6 text-center"><AlertTriangle className="h-10 w-10 mx-auto text-amber-500 mb-2" /><p className="text-sm">{err}</p></div>
        ) : !info ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : done ? (
          <div className="rounded-xl border bg-white dark:bg-slate-800 p-8 text-center" data-testid="reg-photo-done">
            <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500 mb-2" />
            <h2 className="text-lg font-semibold">Photo sent</h2>
            <p className="text-sm text-slate-500">It's attached on your computer. You can close this page.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <h1 className="text-xl font-bold">{info.question_label}</h1>
              <p className="text-sm text-slate-500">{info.form_name}{info.for_label ? ` · ${info.for_label}` : ''}</p>
            </div>
            <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden"
              onChange={e => { const f = e.target.files?.[0]; if (f) setShot({ file: f, url: URL.createObjectURL(f) }); }} data-testid="reg-photo-input" />
            {shot ? (
              <img src={shot.url} alt="Your photo" className="w-full rounded-xl border" />
            ) : (
              <button onClick={() => camRef.current?.click()} className="w-full rounded-xl border-2 border-dashed p-10 flex flex-col items-center gap-2" style={{ borderColor: brand, color: brand }} data-testid="reg-photo-take">
                <Camera className="h-10 w-10" /><span className="font-medium">Take photo</span>
                <span className="text-xs text-slate-500">Lay it flat in good light</span>
              </button>
            )}
            {sendErr && <p className="text-sm text-red-600" data-testid="reg-photo-err">{sendErr}</p>}
            {shot && (
              <div className="grid grid-cols-2 gap-2">
                <button className="h-12 rounded-xl border" onClick={() => { setShot(null); setSendErr(''); camRef.current?.click(); }}>Retake</button>
                <button className="h-12 rounded-xl text-white font-medium flex items-center justify-center gap-2" style={{ background: brand }} onClick={send} disabled={busy} data-testid="reg-photo-send">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Send
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
